import {
  useState,
  useCallback,
  useEffect,
  memo,
  ReactNode,
  useRef,
} from 'react';
import { MediaContext } from '../media.context';
import { MediaItem } from '../media.types';
import { RootWrapper } from '../media.styled';
import { useFileDownload } from '../hooks/use-file-download';

type RootProps = {
  initialItems?: MediaItem[];
  onItemsChange?: (
    items: MediaItem[],
    meta?: {
      action: 'add' | 'remove';
      itemId?: string;
    }
  ) => void;
  canDelete?: boolean;
  children: ReactNode;
};

export const Root = memo(
  ({
    initialItems = [],
    onItemsChange,
    canDelete = false,
    children,
  }: RootProps) => {
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const scrollPrevRef = useRef<() => void>(() => {});
    const scrollNextRef = useRef<() => void>(() => {});

    const download = useFileDownload();

    const registerCarouselScroll = useCallback(
      (prev: () => void, next: () => void): void => {
        scrollPrevRef.current = prev;
        scrollNextRef.current = next;
      },
      []
    );

    const setCarouselScrollState = useCallback(
      (canPrev: boolean, canNext: boolean): void => {
        setCanScrollPrev(canPrev);
        setCanScrollNext(canNext);
      },
      []
    );

    const closePreview = useCallback((): void => {
      setPreviewIndex(null);
    }, []);

    const openPreview = useCallback((index: number): void => {
      setPreviewIndex(index);
    }, []);

    const navigatePreview = useCallback(
      (dir: -1 | 1): void => {
        setPreviewIndex((prev) => {
          if (prev === null) {
            return null;
          }

          const next = prev + dir;

          if (next < 0 || next >= initialItems.length) {
            return prev;
          }

          return next;
        });
      },
      [initialItems.length]
    );

    const addItems = useCallback(
      (newItems: MediaItem[]): void => {
        onItemsChange?.([...initialItems, ...newItems], {
          action: 'add',
        });
      },
      [initialItems, onItemsChange]
    );

    const removeItem = useCallback(
      (id: MediaItem['id']): void => {
        const next = initialItems.filter((item) => item.id !== id);

        onItemsChange?.(next, {
          action: 'remove',
          itemId: id,
        });

        setPreviewIndex((prev) => {
          if (prev === null) {
            return null;
          }

          if (!next.length) {
            return null;
          }

          if (prev >= next.length) {
            return next.length - 1;
          }

          return prev;
        });
      },
      [initialItems, onItemsChange]
    );

    useEffect(() => {
      if (previewIndex === null) {
        return;
      }

      const handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'ArrowLeft') {
          navigatePreview(-1);
        }

        if (e.key === 'ArrowRight') {
          navigatePreview(1);
        }

        if (e.key === 'Escape') {
          closePreview();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [previewIndex, navigatePreview, closePreview]);

    return (
      <MediaContext.Provider
        value={{
          items: initialItems,
          canDelete,
          addItems,
          removeItem,
          previewIndex,
          openPreview,
          closePreview,
          navigatePreview,
          download,
          canScrollPrev,
          canScrollNext,
          scrollPrev: () => scrollPrevRef.current(),
          scrollNext: () => scrollNextRef.current(),
          registerCarouselScroll,
          setCarouselScrollState,
        }}
      >
        <RootWrapper>{children}</RootWrapper>
      </MediaContext.Provider>
    );
  }
);
